import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { translateDynamicContent } from '@/i18n';
import { configFontStack } from '@/services/tenant/tenantFont';
import { withCurrentCopyrightYear } from '@/utils/copyrightYear';
import { useTenantStore } from '@/store/tenantStore';
import { getLogoImageUrl, getWhiteLogoUrl } from '@/services/tenant/tenantImages';
import styles from './DynamicHeaderFooter.module.css';
import type {
  DynamicHeaderFooter,
  DynamicLayoutConfig,
  DynamicWidgetNode,
  HeaderFooterConfig,
} from '@/types/tenant.types';

/**
 * Tenant-configured header / footer — mirrors Flutter feature_modules/dynamic_footer
 * (DynamicHeader, DynamicFooter, DynamicHeaderFooterModel).
 *
 * Config comes from GET v1/p/language/constants (language: headerFooterConfig).
 * Mobile uses the *ForMobile configs below the app's mobile breakpoint (styles/breakpoints.ts).
 * Flutter switches at 600px (HeaderFooterConfigModel.getFooterConfig) and 450px for the header
 * (WebPageHeader isMobile), but tenants' web layouts need desktop width, so 600–767px (phones
 * in landscape) gets the mobile ones here, matching the compact AppHeader.
 */

/** Viewport-dependent config pick (Flutter getHeaderConfig / getFooterConfig). */
export function useDynamicConfig(
  config: HeaderFooterConfig | null | undefined,
  part: 'header' | 'footer',
): DynamicHeaderFooter | undefined {
  const isMobile = useIsMobile();
  if (!config) return undefined;
  if (part === 'header') return isMobile ? config.headerConfigForMobile : config.headerConfigForWeb;
  return isMobile ? config.footerConfigForMobile : config.footerConfigForWeb;
}

/** Flutter Color(int.parse(value, radix: 16)) — ARGB hex, e.g. "ff1f2937". */
function argbToCss(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const hex = value.replace('#', '');
  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(hex)) return fallback;
  if (hex.length === 6) return `#${hex}`;
  const alpha = parseInt(hex.slice(0, 2), 16) / 255;
  const r = parseInt(hex.slice(2, 4), 16);
  const g = parseInt(hex.slice(4, 6), 16);
  const b = parseInt(hex.slice(6, 8), 16);
  return `rgba(${r}, ${g}, ${b}, ${Number(alpha.toFixed(3))})`;
}

/** Flutter ColorsConstants.hexToColor — only 6-digit RGB hex (with or without '#'). */
function rgbHexToCss(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const hex = value.replace('#', '');
  return /^[0-9a-fA-F]{6}$/.test(hex) ? `#${hex}` : undefined;
}

/** Flutter WebFontConstants.getFontWeightBy */
function fontWeight(value: unknown): CSSProperties['fontWeight'] {
  const v = String(value ?? '600');
  if (/^[1-9]00$/.test(v)) return Number(v);
  if (v === 'bold' || v === 'normal') return v;
  return undefined;
}

const num = (value: unknown, fallback: number): number =>
  typeof value === 'number' ? value : fallback;

interface ResolvedConfig {
  height: number;
  background: string;
  backgroundImage?: string;
  textColor: string;
  linkColor: string;
  fontSize: number;
  /** CSS stack for config.fontFamily; undefined → inherit the tenant font */
  fontFamily?: string;
  spacing: number;
  padding: number;
  margin: string;
  /** Alt text for an Image node: the tenant name for its logo, '' (decorative) for anything else */
  imageAlt: (url: string) => string;
}

function resolveConfig(
  config: DynamicLayoutConfig | undefined,
  imageAlt: (url: string) => string = () => '',
): ResolvedConfig {
  const m = config?.margin ?? {};
  return {
    imageAlt,
    height: num(config?.footerHeight, 200),
    background: argbToCss(config?.backgroundColor, 'transparent'),
    backgroundImage: config?.backgroundImage,
    textColor: argbToCss(config?.textColor, '#000000'),
    linkColor: argbToCss(config?.linkColor, '#000000'),
    fontSize: num(config?.fontSize, 14),
    fontFamily: configFontStack(config?.fontFamily),
    spacing: num(config?.spacing, 16),
    padding: num(config?.padding, 24),
    margin: `${m.top ?? 0}px ${m.right ?? 0}px ${m.bottom ?? 0}px ${m.left ?? 0}px`,
  };
}

const JUSTIFY: Record<string, CSSProperties['justifyContent']> = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  spaceBetween: 'space-between',
  spaceAround: 'space-around',
  spaceEvenly: 'space-evenly',
};

const ALIGN: Record<string, CSSProperties['alignItems']> = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  stretch: 'stretch',
  baseline: 'baseline',
};

/** Flutter launchUrl on web opens every link in a new tab. */
export function openLink(url: string): void {
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

function childrenOf(node: DynamicWidgetNode): DynamicWidgetNode[] {
  return Array.isArray(node.children) ? node.children : [];
}

type Part = 'header' | 'footer';

/**
 * Where a node sits, so Rows and Columns size like Flutter's:
 *   row     — inside a Row (or the header's top-level flex row)
 *   column  — inside a Column, or at the top of the footer
 * `bounded` is false for a Column inside a Row: Flutter gives a Row's non-flex children
 * unbounded width, so Rows within that Column shrink to their content.
 */
interface Slot {
  parent: 'row' | 'column';
  bounded: boolean;
}

const IN_ROW: Slot = { parent: 'row', bounded: false };

const nodeType = (node: DynamicWidgetNode) => node.fieldType ?? node.type;

/** A Row is mainAxisSize.max, so with bounded width it fills it — and so does a Column holding one. */
function fillsWidth(node: DynamicWidgetNode): boolean {
  const type = nodeType(node);
  if (type === 'Row') return true;
  return type === 'Column' && childrenOf(node).some(fillsWidth);
}

function DynamicNode({ node, config, part, slot }: { node: DynamicWidgetNode; config: ResolvedConfig; part: Part; slot: Slot }) {
  const type = nodeType(node);
  const style = (node.textStyle ?? undefined) as Record<string, unknown> | undefined;
  const stretch = slot.parent === 'column' && slot.bounded && fillsWidth(node);

  switch (type) {
    case 'Row': {
      const justify = JUSTIFY[String(node.mainAxisAlignment ?? 'start')] ?? 'flex-start';
      const spreads = justify === 'space-between' || justify === 'space-around' || justify === 'space-evenly';
      // Header rows wrap when the width runs out. Footer rows behave like Flutter's Row from
      // 1024px up (no wrapping, see DynamicHeaderFooter.module.css) and wrap below.
      const footerRowClass = part === 'footer'
        ? `${styles.footerRow} ${spreads ? styles.footerRowSpread : ''}`
        : undefined;
      return (
        <div
          className={footerRowClass}
          style={{
            // Flutter wraps Rows in Flexible, which only shares space along a parent Row
            flex: slot.parent === 'row' ? num(node.flex, 1) : undefined,
            alignSelf: stretch ? 'stretch' : undefined,
            display: 'flex',
            flexWrap: part === 'footer' ? undefined : 'wrap',
            justifyContent: justify,
            alignItems: ALIGN[String(node.crossAxisAlignment ?? 'center')] ?? 'center',
          }}
        >
          {childrenOf(node).map((child, i) => (
            <DynamicNode key={i} node={child} config={config} part={part} slot={IN_ROW} />
          ))}
        </div>
      );
    }
    case 'Column':
      return (
        <div
          style={{
            alignSelf: stretch ? 'stretch' : undefined,
            display: 'flex',
            flexDirection: 'column',
            alignItems: ALIGN[String(node.crossAxisAlignment ?? 'center')] ?? 'center',
          }}
        >
          {childrenOf(node).map((child, i) => (
            <DynamicNode
              key={i}
              node={child}
              config={config}
              part={part}
              slot={{ parent: 'column', bounded: slot.parent === 'column' && slot.bounded }}
            />
          ))}
        </div>
      );
    case 'Text':
      return (
        // pre-wrap keeps edge spaces that Flutter renders, e.g. "Myntra" + " is proud…"
        <span style={{ ...textStyle(style, config, config.textColor, part), opacity: num(node.opacity, 1), whiteSpace: 'pre-wrap' }}>
          {/* A tenant copyright line ("© 2025 …") shows the current year */}
          {withCurrentCopyrightYear(translateDynamicContent(String(node.text ?? '')))}
        </span>
      );
    case 'Link': {
      const url = String(node.url ?? '');
      return (
        <button
          type="button"
          onClick={() => openLink(url)}
          style={{
            ...textStyle(style, config, part === 'footer' ? config.linkColor : config.textColor, part),
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: part === 'header' ? '4px 8px' : `0 0 ${config.spacing / 2}px 0`,
            textAlign: 'left',
          }}
        >
          {translateDynamicContent(String(node.label ?? ''))}
        </button>
      );
    }
    case 'Image':
      // Flutter DynamicHeader skips images; the footer renders them
      if (part === 'header') return null;
      return (
        <img
          src={String(node.url ?? '')}
          alt={config.imageAlt(String(node.url ?? ''))}
          style={{ height: num(node.height, 40), width: num(node.width, 120), objectFit: 'contain' }}
        />
      );
    case 'SocialIcon': {
      if (part === 'header') return null;
      const size = num(node.size, 24);
      return (
        <button
          type="button"
          onClick={() => openLink(String(node.url ?? ''))}
          aria-label={String(node.platform ?? '')}
          title={String(node.platform ?? '')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px' }}
        >
          <span
            style={{
              display: 'inline-block',
              width: size,
              height: size,
              backgroundColor: config.linkColor,
              WebkitMaskImage: `url("${String(node.icon ?? '')}")`,
              maskImage: `url("${String(node.icon ?? '')}")`,
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
            }}
          />
        </button>
      );
    }
    case 'Location':
      if (part === 'header') return null;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, paddingRight: 16, color: config.textColor, fontSize: config.fontSize, fontFamily: config.fontFamily }}>
          <PinIcon color={config.textColor} />
          {String(node.name ?? '')}
        </span>
      );
    case 'Spacer': {
      const width = num(node.width, 0);
      const height = num(node.height, 0);
      return <span style={{ display: 'inline-block', width: width > 0 ? width : undefined, height: height > 0 ? height : undefined }} />;
    }
    case 'Dropdown':
      if (part === 'footer') return null;
      return <HeaderDropdown node={node} config={config} />;
    default:
      // Flutter CustomWidget returns an empty box; unknown types render nothing
      return null;
  }
}

function textStyle(
  style: Record<string, unknown> | undefined,
  config: ResolvedConfig,
  defaultColor: string,
  part: Part,
): CSSProperties {
  // Footer reads textStyle.textColor (RGB hex); header reads textStyle.color (ARGB hex)
  const color =
    part === 'footer'
      ? rgbHexToCss(style?.textColor) ?? defaultColor
      : style?.color != null
        ? argbToCss(style.color, defaultColor)
        : defaultColor;
  return {
    color,
    fontSize: num(style?.fontSize, config.fontSize),
    // Footer defaults to '600' even without a textStyle; the header only styles when one is given
    fontWeight: part === 'footer' || style ? fontWeight(style?.fontWeight) : undefined,
    // Header without a textStyle uses config.fontFamily; everything else the tenant font
    fontFamily: part === 'header' && !style ? config.fontFamily : undefined,
  };
}

function HeaderDropdown({ node, config }: { node: DynamicWidgetNode; config: ResolvedConfig }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const items = (Array.isArray(node.items) ? node.items : []) as Record<string, unknown>[];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2,
          padding: '4px 8px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: config.linkColor,
          fontSize: num(node.fontSize, config.fontSize),
          fontWeight: node.fontWeight === 'bold' ? 'bold' : 'normal',
          fontFamily: config.fontFamily,
        }}
      >
        {String(node.label ?? '')}
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 40,
            minWidth: 160, padding: '6px 0', background: '#fff',
            borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          }}
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                openLink(String(item.url ?? ''));
              }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px',
                background: 'none', border: 'none', cursor: 'pointer',
                ...textStyle(item.textStyle as Record<string, unknown> | undefined, config, config.textColor, 'header'),
              }}
            >
              {String(item.label ?? '')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PinIcon({ color }: { color: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z" />
    </svg>
  );
}

function layoutOf(model: DynamicHeaderFooter): DynamicWidgetNode[] {
  return model.layout ?? model.rows ?? [];
}

export interface HeaderNavItem {
  label: string;
  url: string;
}

/** A Dropdown's items under its label, or a run of plain Links (no label). */
export interface HeaderNavGroup {
  label?: string;
  items: HeaderNavItem[];
}

/**
 * The header config's Links and Dropdowns, flattened for the mobile menu panel. Labels are
 * translated the same way DynamicHeader renders them (Links yes, Dropdowns as-is).
 */
export function collectHeaderNav(model: DynamicHeaderFooter | undefined): HeaderNavGroup[] {
  if (!model) return [];
  const groups: HeaderNavGroup[] = [];
  let links: HeaderNavItem[] | null = null;
  const walk = (node: DynamicWidgetNode): void => {
    const type = nodeType(node);
    if (type === 'Link') {
      if (!links) {
        links = [];
        groups.push({ items: links });
      }
      links.push({ label: translateDynamicContent(String(node.label ?? '')), url: String(node.url ?? '') });
    } else if (type === 'Dropdown') {
      links = null;
      const items = (Array.isArray(node.items) ? node.items : []) as Record<string, unknown>[];
      groups.push({
        label: String(node.label ?? ''),
        items: items.map((item) => ({ label: String(item.label ?? ''), url: String(item.url ?? '') })),
      });
    } else if (type === 'Row' || type === 'Column') {
      childrenOf(node).forEach(walk);
    }
  };
  layoutOf(model).forEach(walk);
  return groups.filter((g) => g.items.length > 0);
}

/** Flutter DynamicHeader — only the `layout` format renders; anything else is empty. */
export function DynamicHeader({ model }: { model: DynamicHeaderFooter }) {
  const layout = layoutOf(model);
  if (layout.length === 0) return null;
  const config = resolveConfig(model.config);
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', padding: config.padding, margin: config.margin }}>
      {layout.map((node, i) => (
        <DynamicNode key={i} node={node} config={config} part="header" slot={IN_ROW} />
      ))}
    </div>
  );
}

/**
 * Footer images carry no alt text in the config. The tenant's own logo — one of its configured
 * logo URLs, or a file named like a logo (e.g. ".../myntra-logo.png") — is named after the
 * tenant; every other image (location pin, decoration) is decorative.
 */
function useFooterImageAlt(): { imageAlt: (url: string) => string; brand: string } {
  const content = useTenantStore((s) => s.content);
  const brand = content?.contentSettings?.general?.name?.trim() ?? '';
  const general = content?.contentSettings?.general;
  const logos = new Set(
    [general?.logoProperties?.url, general?.logoPropertiesWhite?.url, getLogoImageUrl(content), getWhiteLogoUrl(content)]
      .filter((u): u is string => !!u),
  );
  const isLogo = (url: string) => logos.has(url) || /logo/i.test(url.split(/[?#]/)[0].split('/').pop() ?? '');
  return { imageAlt: (url) => (url && isLogo(url) ? brand : ''), brand };
}

/** Flutter DynamicFooter — `layout` rows, or the legacy sections format. */
export function DynamicFooter({ model }: { model: DynamicHeaderFooter }) {
  const { t } = useTranslation();
  const { imageAlt, brand } = useFooterImageAlt();
  const config = resolveConfig(model.config, imageAlt);
  const layout = layoutOf(model);

  if (layout.length > 0) {
    return (
      <footer
        style={{
          position: 'relative',
          minHeight: config.height,
          background: config.background,
          boxShadow: '0 0 5px 2px rgba(158,158,158,0.5)',
          overflow: 'hidden',
        }}
      >
        {config.backgroundImage && (
          <img
            src={config.backgroundImage}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {/* Flutter overlays the background colour at 92% over the image */}
        <div style={{ position: 'absolute', inset: 0, background: config.background, opacity: 0.92 }} />
        <div
          style={{
            position: 'relative',
            minHeight: config.height,
            padding: config.padding,
            margin: config.margin,
            boxSizing: 'border-box',
          }}
        >
          {/* Flutter: Column(crossAxisAlignment: start) inside the padded container, across the
              full width (the tenant's layout spaces its own content, e.g. with Spacers) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              minHeight: `calc(${config.height}px - ${2 * config.padding}px)`,
            }}
          >
            {layout.map((node, i) => (
              <DynamicNode key={i} node={node} config={config} part="footer" slot={{ parent: 'column', bounded: true }} />
            ))}
          </div>
        </div>
      </footer>
    );
  }

  const sections = model.sections ?? [];
  const social = model.socialLinks ?? [];
  const locations = model.locations ?? [];
  const sectionBlocks: ReactNode[] = sections.map((section, i) => (
    <div key={i} style={{ width: 150, display: 'flex', flexDirection: 'column' }}>
      <strong style={{ color: config.textColor, fontSize: config.fontSize, fontFamily: config.fontFamily, paddingBottom: config.spacing / 2 }}>
        {section.title}
      </strong>
      {section.links.map((link, j) => (
        <button
          key={j}
          type="button"
          onClick={() => openLink(link.url)}
          style={{ background: 'none', border: 'none', padding: `0 0 ${config.spacing / 2}px 0`, textAlign: 'left', cursor: 'pointer', color: config.linkColor, fontSize: config.fontSize - 1, fontFamily: config.fontFamily }}
        >
          {link.label}
        </button>
      ))}
    </div>
  ));

  return (
    <footer style={{ background: config.background, padding: config.padding, margin: config.margin }}>
      {(model.logoUrl || social.length > 0) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: config.spacing }}>
          {model.logoUrl ? <img src={model.logoUrl} alt={brand} style={{ height: 40 }} /> : <span />}
          {social.length > 0 && (
            <div style={{ display: 'flex' }}>
              {social.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => openLink(s.url)}
                  aria-label={s.platform}
                  title={s.platform}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 8px', color: config.linkColor }}
                >
                  {s.icon ? <img src={s.icon} alt="" width={24} height={24} /> : t(s.platform)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {locations.length > 0 && (
        <div style={{ display: 'flex', paddingBottom: config.spacing }}>
          {locations.map((loc, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, paddingRight: 16, color: config.textColor, fontSize: config.fontSize, fontFamily: config.fontFamily }}>
              <PinIcon color={config.textColor} />
              {loc.name}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: config.spacing * 2, rowGap: config.spacing }}>
        {sectionBlocks}
      </div>
      {model.copyrightText && (
        <p style={{ margin: `${config.spacing}px 0 0`, color: config.textColor, opacity: 0.7, fontSize: config.fontSize - 2, fontFamily: config.fontFamily }}>
          {withCurrentCopyrightYear(model.copyrightText)}
        </p>
      )}
    </footer>
  );
}
